import type { FormikErrors, FormikTouched } from 'formik';
import z, { ZodError } from 'zod';

import { categoryEditorSchema } from '@/schemas';
import type { CategoryEditorInterface, CategoryJob } from '@/types/categoriesEditor';

export const fieldErrorNoteFactory =
    (touched: FormikTouched<CategoryEditorInterface>, errors: FormikErrors<CategoryEditorInterface>) =>
    (fieldName: keyof CategoryEditorInterface) =>
        touched[fieldName] && errors[fieldName] ? errors[fieldName] : undefined;

export const sortCategoryJobs = (jobs: CategoryJob[]): CategoryJob[] =>
    [...jobs].sort((a, b) => {
        const inProgressOrder = (b.status === 'processing' ? 1 : 0) - (a.status === 'processing' ? 1 : 0);
        if (inProgressOrder !== 0) {
            return inProgressOrder;
        }

        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

const keysToCompare: (keyof CategoryJob)[] = [
    'jobId',
    'status',
    'processedWebStyleIDCount',
    'successWebStyleIDCount',
    'errorWebStyleIDCount',
    'updatedAt',
];

export const haveCategoryJobsChanged = (prevJobs: CategoryJob[], newJobs: CategoryJob[]): boolean => {
    if (prevJobs.length !== newJobs.length) {
        return true;
    }

    return prevJobs.some((prevJob, idx) => {
        const newJob = newJobs[idx];
        return keysToCompare.some(key => prevJob[key] !== newJob[key]);
    });
};

export const validateCategoryForm = (values: CategoryEditorInterface) => {
    try {
        categoryEditorSchema.parse(values);
        return {};
    } catch (error) {
        if (error instanceof ZodError) {
            const fieldErrors = z.flattenError(error).fieldErrors as Record<string, string[] | undefined>;

            return Object.keys(fieldErrors).reduce(
                (acc, field) => {
                    acc[field] = fieldErrors[field]?.[0] ?? 'Invalid value';
                    return acc;
                },
                {} as Record<string, string>
            );
        }
    }
    return {};
};
